import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { boards, posts } from '@/mocks/community';
import type { Post } from '@/mocks/community';

const sortOptions = [
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '最热' },
  { key: 'replies', label: '回复最多' },
] as const;

type SortKey = (typeof sortOptions)[number]['key'];

export default function Community() {
  const [activeBoard, setActiveBoard] = useState<number>(0);
  const [sort, setSort] = useState<SortKey>('latest');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    let list: Post[] = [...posts];
    if (activeBoard !== 0) list = list.filter((p) => p.boardId === activeBoard);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.board.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    if (sort === 'hot') list = [...list].sort((a, b) => Number(b.hot) - Number(a.hot) || b.views - a.views);
    if (sort === 'replies') list = [...list].sort((a, b) => b.replies - a.replies);
    return list;
  }, [activeBoard, sort, query]);

  return (
    <Layout>
      <section className="relative overflow-hidden bg-foreground-900 pt-24 md:pt-28 pb-12 md:pb-16 text-center">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Stylized%20anime%20illustration%20of%20colorful%20speech%20bubbles%20and%20chat%20icons%20floating%20over%20a%20warm%20sunset%20sky%2C%20soft%20coral%20amber%20and%20peach%20tones%2C%20flat%20vibrant%20anime%20art%20style%2C%20playful%20and%20energetic%20community%20vibe%2C%20clean%20composition%20with%20soft%20glow%20and%20sparkles&width=1600&height=500&seq=community-banner&orientation=landscape"
            alt="讨论社区背景插画"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl text-background-50">讨论社区</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm md:text-base text-background-100/90">
            板块与热帖都在这里，找到兴趣圈子，一起讨论剧情、安利作品、寻找同好。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveBoard(0)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeBoard === 0
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部板块
              </button>
              {boards.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setActiveBoard(b.id)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeBoard === b.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {b.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <div className="relative flex-1 lg:flex-none">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索帖子、标签…"
                  className="w-full lg:w-56 rounded-lg border border-background-200 bg-background-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div className="flex items-center gap-1 rounded-lg bg-background-100 p-1">
                {sortOptions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setSort(o.key)}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                      sort === o.key
                        ? 'bg-background-50 text-primary-700 font-semibold shadow-sm'
                        : 'text-foreground-600'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-col gap-3">
            {filtered.length === 0 && (
              <div className="rounded-2xl border border-background-200 bg-background-50 py-16 text-center text-sm text-foreground-500">
                没有找到相关帖子，换个关键词试试吧
              </div>
            )}
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/post/${p.id}`}
                className="group flex flex-col gap-3 rounded-2xl border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between hover:border-primary-300 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                      <i className={`${boards.find((b) => b.id === p.boardId)?.icon || 'ri-discuss-fill'} text-xs`}></i>
                      {p.board}
                    </span>
                    {p.pinned && (
                      <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-700">
                        <i className="ri-pushpin-fill mr-0.5"></i>置顶
                      </span>
                    )}
                    {p.hot && (
                      <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs text-accent-700">
                        <i className="ri-fire-fill mr-0.5"></i>热门
                      </span>
                    )}
                  </div>
                  <h2 className="mt-2 truncate text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-1 hidden sm:block text-sm text-foreground-500 line-clamp-1">{p.excerpt}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                    <span className="font-medium text-foreground-700">{p.author}</span>
                    <span>{p.time}</span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-5 text-xs text-foreground-500 md:flex-col md:items-end md:gap-1">
                  <span><i className="ri-chat-3-line mr-1"></i>{p.replies} 回复</span>
                  <span><i className="ri-eye-line mr-1"></i>{p.views.toLocaleString()}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}